import { useTranslation } from 'common/hooks/useTranslation';
import { Close } from 'common/components/icons/Close';
import { InstallationButton } from 'app/installation/components/Installation/InstallationButton';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
} from '@mui/material';

import { PropertyFiberChecklist as PropertyFiberChecklistAPI } from '../api';
import { useProperty } from '../hooks/useProperty';
import { useFiberChecklist } from '../hooks/useFiberChecklist';

export type PropertyChecklistProps = {
  id: number | null;
  show: boolean;
  onShow: (show: boolean) => void;
};

export const PropertyChecklist: FC<PropertyChecklistProps> = (props) => {
  const { t } = useTranslation();

  const { id, show, onShow } = props;
  const { data } = useProperty(id);

  const {
    data: fiberChecklistByPropertyIdResponse,
    updatePropertyFiberChecklist,
    createPropertyFiberChecklist,
    isMutationsLoading,
  } = useFiberChecklist(id, {
    onCreateSuccess: () => onShow(false),
    onUpdateSuccess: () => onShow(false),
  });

  const [checkboxes, setCheckboxes] = useState<PropertyFiberChecklistAPI>({
    wire: false,
    fire: false,
    duct: false,
    clean: false,
    node: false,
  });

  const handleDialogClose = useCallback(() => {
    onShow(false);
  }, [onShow]);

  useEffect(() => {
    if (fiberChecklistByPropertyIdResponse && fiberChecklistByPropertyIdResponse.fiberChecklistByPropertyId) {
      setCheckboxes(fiberChecklistByPropertyIdResponse.fiberChecklistByPropertyId.checklist);
    }
  }, [fiberChecklistByPropertyIdResponse]);

  const propertyId = useMemo(() => data?.fiberChecklistByPropertyId, [data]);

  const handleDialogConfirm = useCallback(() => {
    if (propertyId) {
      updatePropertyFiberChecklist(checkboxes);
    } else {
      createPropertyFiberChecklist(checkboxes);
    }
  }, [propertyId, updatePropertyFiberChecklist, createPropertyFiberChecklist]);

  const handleCheckboxChange = (name: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setCheckboxes({ ...checkboxes, [name]: event.target.checked });
  };

  return (
    <Dialog open={show} onClose={handleDialogClose} PaperProps={{ sx: { margin: 0.5, p: 2 } }}>
      <DialogTitle>{t('checklist.title')}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => {
          onShow(false);
        }}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
        }}
      >
        <Close />
      </IconButton>
      <DialogContent>
        <FormControl component="fieldset">
          <FormGroup>
            <FormControlLabel
              control={<Checkbox checked={checkboxes.wire} onChange={handleCheckboxChange('wire')} />}
              label={t('checklist.property.wire')}
            />
            <FormControlLabel
              control={<Checkbox checked={checkboxes.fire} onChange={handleCheckboxChange('fire')} />}
              label={t('checklist.property.fire')}
            />
            <FormControlLabel
              control={<Checkbox checked={checkboxes.duct} onChange={handleCheckboxChange('duct')} />}
              label={t('checklist.property.duct')}
            />
            <FormControlLabel
              control={<Checkbox checked={checkboxes.clean} onChange={handleCheckboxChange('clean')} />}
              label={t('checklist.property.clean')}
            />
            <FormControlLabel
              control={<Checkbox checked={checkboxes.node} onChange={handleCheckboxChange('node')} />}
              label={t('checklist.property.node')}
            />
          </FormGroup>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Grid
          container
          sx={{
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Button variant="contained" color="primary" onClick={handleDialogConfirm} disabled={isMutationsLoading}>
            {isMutationsLoading ? <CircularProgress size={16} /> : t('general.save')}
          </Button>
          <InstallationButton
            propertyId={id}
            onStartClick={handleDialogConfirm}
            onEndClick={handleDialogConfirm}
            disabled={isMutationsLoading}
          />
        </Grid>
      </DialogActions>
    </Dialog>
  );
};
